import { getFadeColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { FormIcon, PlusIcon } from '@repo/ui/icons'
import { useRef, useState } from 'react'

import { API } from '../../../api/apis.config'
import CountryFlag from '../../../components/elements/country-flag'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import { EnrollmentsFilter } from '../../../components/pages/enrollments/enrollment-filter'
import { Paths } from '../../../lib/constants/path'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { ApplicationStatusEnum } from '../../../lib/types/enums/enrollment/application-status.enum'
import {
  enrollmentQuoteListFields,
  EnrollmentQuoteListType
} from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import {
  transformEnrollmentsFiltersToUrl,
  transformUrlToEnrollmentFilters
} from '../../../lib/utils/filters-mapping/enrollment'

const EnrollmentQuotationListPage = () => {
  const [isTableLoading, setIsTableLoading] = useState(false)

  const tableRef = useRef<DynamicTableRef<EnrollmentQuoteListType>>(null)
  const { navigate, navigateWithEvent } = useNavigator()

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<EnrollmentFilter>({
      initialFilters: {
        statuses: [ApplicationStatusEnum.QUOTE]
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformEnrollmentsFiltersToUrl,
      transformUrlToFilter: transformUrlToEnrollmentFilters
    })

  const columns: Column<EnrollmentQuoteListType>[] = [
    {
      accessorKey: 'id',
      header: 'Numéro de devis',
      enableSorting: true
    },
    {
      accessorKey: 'childFirstName',
      header: 'NOM du Participant',
      enableSorting: true,
      cell: ({ row: { original } }) =>
        original.childFirstName && original.childLastName
          ? toFullName(original.childFirstName, original.childLastName, {
              options: { mode: 'lastname-firstname' }
            })
          : '-'
    },

    {
      accessorKey: EnrollmentProperties.getEnrollmentCountry().key,
      header: 'Pays',
      enableSorting: true,
      cell: ({ row }) => {
        const value = EnrollmentProperties.getEnrollmentCountry(row.original).value
        if (!value) return '-'
        return <CountryFlag code={value} />
      }
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentDates().key,
      header: 'Dates de séjour',
      enableSorting: true,
      cell: ({ row }) => {
        const value = EnrollmentProperties.getEnrollmentDates(row.original).value
        if (!value) return '-'
        const { startDate, endDate, color } = value
        return (
          <Badge
            text={`${startDate} - ${endDate}`}
            color={getFadeColor(color)}
            textColor={color}
          />
        )
      }
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentFormula().key,
      header: 'Formule',
      enableSorting: true,
      cell: ({ row }) => EnrollmentProperties.getEnrollmentFormula(row.original).value ?? '-'
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentMainOption().key,
      header: 'Thème',
      enableSorting: true,
      cell: ({ row }) => EnrollmentProperties.getEnrollmentMainOption(row.original).value ?? '-'
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentOptions().key,
      header: 'Option facultative',
      cell: ({ row }) => EnrollmentProperties.getEnrollmentOptions(row.original).value
    },

    {
      accessorKey: 'createdAt',
      header: 'Date de demande',
      enableSorting: true,
      cell: (data) => (
        <Badge variant="grey" text={DateFormatter.DDMMYYYY(data.row.original.createdAt)} />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Liste des devis" icon={FormIcon} />
      <div className="h-4" />
      <DynamicDataTable<EnrollmentQuoteListType, EnrollmentFilter>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Toutes les devis"
        columns={columns}
        version="PARTIAL"
        deleteMessage={{
          name: 'devis',
          attributeName: 'id',
          // use child first and last name to display the name of the enrollment
          deleteOne: (enrollment) =>
            `Voulez-vous vraiment supprimer le devis de ${toFullName(enrollment.childFirstName, enrollment.childLastName)} ?`,
          deleteMany: (enrollments) =>
            `Voulez-vous vraiment supprimer ${enrollments.length} devis ?`,
          deleteAll: 'Voulez-vous vraiment supprimer tous les devis ?'
        }}
        selectable
        allowSearchParam
        extraParams={{ fields: enrollmentQuoteListFields }}
        endpoint={API.enrollments.getAll.url}
        onRowClick={(data, event) =>
          navigateWithEvent(
            Paths.ENROLLMENT_QUOTATION_UPDATE_PAGE.replace(':id', data.id.toString()),
            event
          )
        }
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, ['statuses'])}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter un devis',
            fn: () => navigate(Paths.ENROLLMENT_QUOTATION_CREATE_PAGE)
          }
        ]}
      />
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <EnrollmentsFilter version="quote" filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default EnrollmentQuotationListPage
